// TMOS Setup Wizard - Hierarchy Selection Step
// Date: December 19, 2025
// Purpose: Hierarchy selection/assignment step for setup wizard

import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import api from '@/services/api';

interface HierarchySelectionStepProps {
  setupData: Record<string, any>;
  onComplete: (data: Record<string, any>) => void;
  onNext: () => void;
  loading: boolean;
}

export const HierarchySelectionStep: React.FC<HierarchySelectionStepProps> = ({
  setupData,
  onComplete,
  loading
}) => {
  const { t } = useLanguage();
  const [hierarchies, setHierarchies] = useState<any[]>([]);
  const [selectedHierarchies, setSelectedHierarchies] = useState<string[]>(
    setupData.selectedHierarchies || []
  );

  useEffect(() => {
    loadHierarchies();
  }, []);

  const loadHierarchies = async () => {
    try {
      // Load available hierarchies (geographic, functional, business)
      const [geoRes, funcRes, busRes] = await Promise.all([
        api.get('/generic/geographic-hierarchies').catch(() => ({ data: { data: [] } })),
        api.get('/generic/functional-hierarchies').catch(() => ({ data: { data: [] } })),
        api.get('/generic/business-hierarchies').catch(() => ({ data: { data: [] } }))
      ]);

      const allHierarchies = [
        ...(geoRes.data.data || []).map((h: any) => ({ ...h, type: 'geographic' })),
        ...(funcRes.data.data || []).map((h: any) => ({ ...h, type: 'functional' })),
        ...(busRes.data.data || []).map((h: any) => ({ ...h, type: 'business' }))
      ];

      setHierarchies(allHierarchies);
    } catch (error) {
      console.error('Error loading hierarchies:', error);
    }
  };

  const handleToggleHierarchy = (hierarchyId: string) => {
    setSelectedHierarchies(prev =>
      prev.includes(hierarchyId)
        ? prev.filter(id => id !== hierarchyId)
        : [...prev, hierarchyId]
    );
  };

  const handleContinue = () => {
    onComplete({
      hierarchy_selection: {
        selectedHierarchies,
        hierarchies: hierarchies.filter(h => selectedHierarchies.includes(h.id))
      },
      selectedHierarchies
    });
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600 mb-6">
        {t('setupWizard.hierarchy.description') || 
          'Select the organizational hierarchies you want to use. You can customize these later.'}
      </p>

      <div className="space-y-4">
        {hierarchies.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {t('setupWizard.hierarchy.noHierarchies') || 'No hierarchies available. Default hierarchies will be used.'}
          </div>
        ) : (
          hierarchies.map((hierarchy) => (
            <div
              key={hierarchy.id}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={selectedHierarchies.includes(hierarchy.id)}
                  onChange={() => handleToggleHierarchy(hierarchy.id)}
                />
                <div>
                  <div className="font-medium text-gray-900">{hierarchy.name}</div>
                  <div className="text-sm text-gray-500 capitalize">{hierarchy.type}</div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="flex justify-end">
        <Button onClick={handleContinue} disabled={loading}>
          {t('common.continue') || 'Continue'}
        </Button>
      </div>
    </div>
  );
};

